import './bootstrap';
import '../css/app.css';

// axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import Swal from 'sweetalert2';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import { createI18n } from 'vue-i18n';
import { useFavicon } from '@vueuse/core';
import { USER_PERMISSIONS } from './constants';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import { loadLocaleMessages } from './dynamicI18n';
import AppHead from '@/Components/AppHead.vue';
import { applyTheme, isSavedThemeDark } from '@/Composables/useTheme.js';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';
import '@vuepic/vue-datepicker/dist/main.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'tippy.js/dist/tippy.css'; // optional for styling


import en from '../lang/i18n/en.json' // Preload English to avoid first-render missing key warnings
//import de from '../lang/i18n/de.json'
//import fr from '../lang/i18n/fr.json' // Import the French JSON file
//import es from '../lang/i18n/es.json'
//import pt from '../lang/i18n/pt.json'
//import it from '../lang/i18n/it.json'
//import el from '../lang/i18n/el.json'
//import nl from '../lang/i18n/nl.json'
//import ro from '../lang/i18n/ro.json'

const defaultLocale = localStorage.getItem('locale') || 'en';
const i18n = new createI18n({
	locale: defaultLocale,
	fallbackLocale: 'en',
	messages: { en },
	legacy: false,
	runtimeOnly: false
})
useFavicon('/favicon.ico?v=keen-2');

function ucfirst(str) {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const ucfirstMixin = {
  methods: {
    ucfirst,
  },
};
const userPermissions = {
  data() {
    return {
      USER_PERMISSIONS: USER_PERMISSIONS,
    };
  },
};

const appName = JSON.parse(document.getElementById('app')?.dataset.page ?? '{}').props?.appName || 'Keen';
const pinia = createPinia();

applyTheme(isSavedThemeDark());

createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : `${appName}`),
  resolve: (name) =>
    resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) });
    if (import.meta.env.VITE_SENTRY_DSN_PUBLIC) {
      try {
        Sentry.init({
          app: app,
          environment: import.meta.env.VITE_APP_ENV,
          dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
          integrations: [
            Sentry.browserTracingIntegration({
              // Disable automatic route change tracking to prevent CORS issues
              instrumentNavigation: false,
              instrumentPageLoad: true,
            }),
          ],
          tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
          // Add allowed origins to prevent CORS issues
          allowUrls: [
            /https:\/\/Keen\.local/,
            /https:\/\/app\.keen\.cc/,
            /https:\/\/.*\.keen\.cc/,
            window.location.origin,
          ],
          // Capture unhandled promise rejections
          captureUnhandledRejections: true,
          // Set a beforeSend hook to filter sensitive data and handle network errors
          beforeSend(event, hint) {
            // Skip if this is a network error from Sentry itself
            if (
              hint.originalException &&
              hint.originalException.message &&
              hint.originalException.message.includes('sentry.io')
            ) {
              return null;
            }
            return event;
          },
          // Add error boundary fallback
          onError: (error) => {
            console.warn('Sentry error:', error);
            return false; // Don't prevent the error from being thrown
          },
        });
      } catch (error) {
        console.warn('Failed to initialize Sentry:', error);
        // Continue without Sentry if initialization fails
      }
    }
    app
      .use(plugin)
      .mixin(ucfirstMixin)
      .mixin(userPermissions)
      .use(ZiggyVue)
      .use(i18n)
      .use(VueGoogleMaps, {
        load: {
          key: import.meta.env.VITE_GOOGLE_API_KEY,
          libraries: 'places',
        },
      })
      .use(pinia)
      .component('AppHead', AppHead)
      .mount(el);

    return app;
  },
  progress: {
    color: 'var(--color-main)',
  },
});

loadLocaleMessages(i18n, defaultLocale);

document.addEventListener('inertia:invalid', (event) => {
    if (event.detail.response.status === 413) {
        event.preventDefault();
        Swal.fire({
            icon: 'error',
            title: 'File Too Large',
            text: 'The file you uploaded is too large. Please choose a smaller file and try again.',
        });
    }
});

let lastUrl = window.location.href;
document.addEventListener('inertia:before', (event) => {
  if (lastUrl.match(/admin\/?$/)) {
    event.preventDefault();

    window.location.href = event.detail.visit.url;
  }

  lastUrl = window.location.href;
});
